// Табът „Съставки" на панела на рецепта: ТЕКУЩАТА рецепта × продаденото —
// теоретичният разход по продукт (под-рецептите разгънати, брутото), по
// текущата себестойност. Надписът над таблицата казва изрично, че това не е
// изписаното (то е в „Продадени продукти" → „Разнесено" и в складовата карта).
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import type { SoldRecipeIngredient } from "../../../../api/inventorySoldAnalysis";
import { formatMoney } from "../../components/numbers";
import { formatQty } from "../../components/qty";
import {
  REPORT_HEAD_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
} from "../components/reportListModel";
import { formatCount } from "../components/soldReportModel";
import { ingredientsTotals, nonZeroIngredients } from "./soldRecipeModel";

const ROW_GRID =
  "grid grid-cols-[minmax(0,1fr)_96px_104px] items-center gap-x-2.5 px-4 @min-[520px]:grid-cols-[minmax(0,1fr)_88px_104px_96px]";
const FROM_MEDIUM = "hidden @min-[520px]:block";

type Props = {
  ingredients: SoldRecipeIngredient[] | undefined;
  isLoading: boolean;
  sold: number; // продадените бройки на рецептата за периода
  decimalsOf: (productId: number) => number;
};

export const SoldRecipeIngredients = ({ ingredients: allIngredients, isLoading, sold, decimalsOf }: Props) => {
  const { t } = useTranslation();
  if (isLoading || !allIngredients) {
    return (
      <div className="flex flex-1 items-center justify-center p-10">
        {isLoading ? <Spin /> : <span className="text-[13px] text-body">{t("inventoryPage.errLoad")}</span>}
      </div>
    );
  }
  // съставка с 0 за периода не се показва
  const ingredients = nonZeroIngredients(allIngredients);
  const totals = ingredientsTotals(ingredients);

  return (
    <div className="@container flex min-h-0 flex-1 flex-col overflow-auto">
      <p className="m-0 border-b border-shade-2 bg-info-tint px-4 py-2.5 text-[12.5px] leading-[1.4] text-info-deep">
        {t("inventoryPage.sdIngredientsNote", { sold: formatCount(sold) })}
      </p>
      {ingredients.length === 0 ? (
        <div className="px-4 py-8 text-center text-[13px] text-body">{t("inventoryPage.sdIngredientsEmpty")}</div>
      ) : (
        <>
          <div className={`${ROW_GRID} ${REPORT_HEADER_ROW_CLASS} sticky top-0 z-[1] bg-panel`}>
            <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.colProductName")}</span>
            <span className={`${REPORT_HEAD_CLASS} ${FROM_MEDIUM} text-right`}>{t("inventoryPage.sdColPerPortion")}</span>
            <span className={`${REPORT_HEAD_CLASS} text-right`}>{t("inventoryPage.sdColTotalQty")}</span>
            <span className={`${REPORT_HEAD_CLASS} text-right`}>{t("inventoryPage.colGross")}</span>
          </div>
          {ingredients.map((ingredient) => {
            const decimals = decimalsOf(ingredient.productId);
            const unit = ingredient.unit ? ` ${ingredient.unit}` : "";
            return (
              <div key={ingredient.productId} className={`${ROW_GRID} ${REPORT_ROW_CLASS}`}>
                <span className="flex min-w-0 flex-col gap-px">
                  <span className="truncate font-semibold text-ink">{ingredient.name}</span>
                  {/* тясна карта: на порция под името */}
                  <span className="truncate font-sheet text-[11.5px] text-muted @min-[520px]:hidden">
                    {t("inventoryPage.sdPerPortionLine", { qty: `${formatQty(ingredient.perPortion, decimals)}${unit}` })}
                  </span>
                </span>
                <span className={`${FROM_MEDIUM} text-right font-sheet tabular-nums text-body`}>
                  {formatQty(ingredient.perPortion, decimals)}
                </span>
                <span className="whitespace-nowrap text-right font-sheet font-semibold tabular-nums text-ink">
                  {formatQty(ingredient.quantity, decimals)}
                  <span className="font-normal text-muted">{unit}</span>
                </span>
                <span className="text-right font-sheet tabular-nums text-ink-2">{formatMoney(ingredient.valueWithVat)}</span>
              </div>
            );
          })}
          <div className={`${ROW_GRID} ${REPORT_TOTAL_ROW_CLASS}`}>
            <span className={`${REPORT_HEAD_CLASS} col-span-2 @min-[520px]:col-span-3`}>
              {t("inventoryPage.sdIngredientsTotal")}
            </span>
            <span className="text-right font-sheet font-bold tabular-nums text-brand-deep-ink">
              {formatMoney(totals.valueWithVat)}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default SoldRecipeIngredients;
